// @ts-strict-ignore
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Form from 'antd/lib/form/Form';
import useForm from 'antd/lib/form/hooks/useForm';
import AntLink from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { SubscriptionFeatureType, User, UserPermissions, UserGroupId } from '../types';
import {
    displayTextForAppPlatform,
    displayTextForPermission,
    displayTextForUserGroupSingular,
    displayUserNameLong,
} from '../helpers/displayHelpers';
import { OrgComponentProps, UserAppData } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import { useDispatch, useSelector } from 'react-redux';
import { OrgSettingsUpdatable } from '../actions/updatable';
import { tryUpdateOrgSettings, UpdateUserRequest } from '../actions/orgSettings';
import { getAvailableAppsFromAppDataMap, userIsAdmin, userIsRunwayAdmin } from '../helpers';
import { RootState } from '../reducers/reducers';
import { selectOrgGroups } from '../selectors/selectors';
import Checkbox from 'antd/lib/checkbox';
import Tooltip from 'antd/lib/tooltip';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface ChangeUserGroupModalProps extends OrgComponentProps {
    open: boolean;
    appDataByAppIdMap: Record<string, UserAppData>;
    onCancel: () => void;
    onSuccess: () => void;
    currentUser?: User;
    directorySyncManagesGroups: boolean;
    isUsingAdHocGroups: boolean;
    disableEditingApps: boolean;
    usePermissionsOnGroupLevel: boolean;
}

const UpdateUserModal: React.FC<ChangeUserGroupModalProps> = (props: ChangeUserGroupModalProps) => {
    const dispatch = useDispatch();
    const [form] = useForm();
    const { Option } = Select;

    const title = props.currentUser ? `Update user '${displayUserNameLong(props.currentUser)}'` : '';

    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateUser);
    const isError = useError(OrgSettingsUpdatable.UpdateUser);
    const isSuccess = useUpdated(OrgSettingsUpdatable.UpdateUser);

    const isAdmin = useSelector((state: RootState) => userIsAdmin(state.user));
    const isRunwayAdmin = useSelector((state: RootState) => userIsRunwayAdmin(state.user));
    const userEmail = useSelector((state: RootState) => state.user.email);
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const disableEditingGroups = props.directorySyncManagesGroups || props.isUsingAdHocGroups;

    const userCanUpdateAdminPermission = (isAdmin && userEmail !== props.currentUser?.email) || isRunwayAdmin;

    const editUserGroups = (values: { groups: UserGroupId[]; permissions: UserPermissions[]; appIds: string[] }) => {
        const updatedPermissions = [];

        // always include runway admin permissions if previously present

        if (props.currentUser?.permissions.includes(UserPermissions.RunwayAdmin)) {
            updatedPermissions.push(UserPermissions.RunwayAdmin);
        }

        if (props.currentUser?.permissions.includes(UserPermissions.RunwayAdminReadOnly)) {
            updatedPermissions.push(UserPermissions.RunwayAdminReadOnly);
        }
        // If permissions UI is hidden because of permissions group levels feature, we're not using the permissions value
        const effectivePermissions =
            props.usePermissionsOnGroupLevel ? (props.currentUser?.permissions ?? []) : (values.permissions ?? []);

        const permissionsSet = new Set([...updatedPermissions, ...effectivePermissions]);

        const requestData: UpdateUserRequest = {
            orgId: props.orgId,
            userId: props.currentUser.id,
            ...values,
            groups: values.groups,
            permissions: Array.from(permissionsSet),
        };

        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateUser, requestData));
    };

    React.useEffect(() => {
        if (isError) {
            globalMessage.error(isError);
        }
    }, [isError]);

    React.useEffect(() => {
        if (isSuccess) {
            globalMessage.success('User updated!');

            props.onCancel();
        }
    }, [isSuccess]);

    const allAvailableApps = Object.values(props.appDataByAppIdMap);
    const userAvailableAppIds = getAvailableAppsFromAppDataMap({
        appDataMap: props.appDataByAppIdMap,
        appIds: props.currentUser?.appIds,
    }).map((app) => app.appId);

    const hasStartedEditingWithAdminPermissions = !!props.currentUser?.permissions?.includes(UserPermissions.Admin);
    const hasStartedEditingWithITAdminPermissions = !!props.currentUser?.permissions?.includes(UserPermissions.ITAdmin);

    return (
        <Modal
            open={props.open}
            title={title}
            okText="Apply"
            onOk={form.submit}
            okButtonProps={{ loading: isLoading }}
            onCancel={props.onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={editUserGroups}
                preserve={false}
                initialValues={{
                    groups: props.currentUser?.groups,
                    permissions: props.currentUser?.permissions,
                    appIds: userAvailableAppIds,
                }}
            >
                <FormItem
                    label="Groups"
                    name="groups"
                    help={
                        disableEditingGroups ?
                            props.directorySyncManagesGroups ?
                                'User groups are being managed by Directory Sync.'
                            : props.isUsingAdHocGroups ?
                                'User groups are determined by ad-hoc groups mapping.'
                            :   null
                        :   null
                    }
                >
                    <Select
                        disabled={disableEditingGroups}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select user group(s)"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={Object.values(orgGroupsById)
                            .filter((userGroup) => !userGroup.disabled)
                            .map((userGroup) => ({
                                label: displayTextForUserGroupSingular(userGroup),
                                value: userGroup.id,
                            }))}
                    ></Select>
                </FormItem>
                <FormItem
                    label="Apps"
                    name="appIds"
                    help={props.disableEditingApps ? 'App membership is being managed by your Directory Sync.' : null}
                >
                    <Select
                        disabled={props.disableEditingApps}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select user app(s)"
                    >
                        {allAvailableApps.map((appData) => {
                            return (
                                <Option value={appData.appId} key={appData.appId}>
                                    {appData.appName}{' '}
                                    <Text type="secondary">
                                        ({displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                    </Text>
                                </Option>
                            );
                        })}
                    </Select>
                </FormItem>
                {!props.usePermissionsOnGroupLevel && (
                    <FormItem
                        label={
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.WriteUsers} requiresTooltip>
                                Permissions
                            </PlanRestrictedFeatureWrapper>
                        }
                        name="permissions"
                    >
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.WriteUsers}>
                            {(isDisabled) =>
                                !userCanUpdateAdminPermission ?
                                    <Tooltip title="You don't have permission to perform this action. Please contact your organization's admin.">
                                        <Checkbox.Group
                                            disabled
                                            options={[
                                                {
                                                    label: displayTextForPermission(UserPermissions.Admin),
                                                    value: UserPermissions.Admin,
                                                    disabled: isDisabled,
                                                },
                                                {
                                                    label: displayTextForPermission(UserPermissions.ITAdmin),
                                                    value: UserPermissions.ITAdmin,
                                                    disabled: isDisabled,
                                                },
                                            ]}
                                        />
                                    </Tooltip>
                                :   <Checkbox.Group
                                        options={[
                                            {
                                                label: displayTextForPermission(UserPermissions.Admin),
                                                value: UserPermissions.Admin,
                                                disabled: hasStartedEditingWithAdminPermissions ? false : isDisabled,
                                            },
                                            {
                                                label: displayTextForPermission(UserPermissions.ITAdmin),
                                                value: UserPermissions.ITAdmin,
                                                disabled: hasStartedEditingWithITAdminPermissions ? false : isDisabled,
                                            },
                                        ]}
                                    />
                            }
                        </PlanRestrictedFeatureWrapper>
                    </FormItem>
                )}
                <AntLink
                    href="https://docs.runway.team/getting-started/setting-up-your-team#user-groups"
                    target="_blank"
                >
                    <QuestionCircleOutlined /> Learn about user group-based permission restrictions
                </AntLink>
            </Form>
        </Modal>
    );
};

export default UpdateUserModal;
